// utils/session.ts
import { useEffect, useState } from 'react';
import { Clock, CheckCircle, ChefHat, XCircle, Utensils, AlertCircle } from 'lucide-react';

export type UserRole = 'customer' | 'owner' | 'manager' | 'dining associate';

export interface SessionObj {
  token: string;
  role: UserRole;
  [extra: string]: any;
}

export function parseJwt(token: string): any | null {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(decodeURIComponent(window.atob(base64).split('').map(c =>
      '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
    ).join('')));
  } catch {
    return null;
  }
}

export function validateRoleAndExpiry(token: string, requiredRole: UserRole): boolean {
  const payload = parseJwt(token);
  if (!payload) return false;
  const jwtRole =
    (payload['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'] ?? payload.role ?? '').toLowerCase();
  const exp = payload.exp;
  return jwtRole === requiredRole.toLowerCase() && exp * 1000 > Date.now();
}

export function isValidCustomerSession(): boolean {
  const raw = JSON.parse(localStorage.getItem('Dinelyt_session') || '{}').token || '';
  if (!raw) return false;
  try {
    const session = JSON.parse(raw);
    if (!session.token) return false;
    const payload = parseJwt(session.token);
    const role =
      (payload && (payload["http://schemas.microsoft.com/ws/2008/06/identity/claims/role"] || payload.role || '')).toLowerCase();
    const exp = payload?.exp;
    if (role !== 'customer') return false;
    if (!exp || exp * 1000 < Date.now()) return false;
    return true;
  } catch {
    return false;
  }
}

// Get user session for a specific role (customer, admin, manager)
export function getUserSession(): SessionObj | null {
  const key = 'Dinelyt_session';
  const raw = localStorage.getItem(key);
  // console.log('getUserSession raw:', raw);
  if (!raw) return null;
  try {
    const obj = JSON.parse(raw) as SessionObj;
    // console.log('getUserSession obj:', obj);
    if (obj.token && validateRoleAndExpiry(obj.token, obj.role)) return obj;
  } catch {
    console.error('Failed to parse session:', raw);
  }
  console.log('getUserSession returning null');
  return null;
}

export function saveUserSession(role: UserRole, session: SessionObj) {
  localStorage.setItem('Dinelyt_session', JSON.stringify(session));
}

export function clearUserSession(role: UserRole) {
  console.log('clearing localstorage from session.ts');
  localStorage.clear();
}


let deviceTypeCache: boolean | null = null;

export const useIsDesktop = (): boolean => {
  const [isDesktop, setIsDesktop] = useState<boolean>(() => {
    return deviceTypeCache !== null ? deviceTypeCache : false;
  });

  useEffect(() => {
    if (deviceTypeCache !== null) return;

    const ua = navigator.userAgent.toLowerCase();
    const isMobileUA = /android|iphone|ipad|ipod|blackberry|iemobile|opera mini/.test(ua);
    const isWideScreen = window.innerWidth >= 768;

    const result = !isMobileUA && isWideScreen;

    deviceTypeCache = result; // Store in memory only
    setIsDesktop(result);
  }, []);

  return isDesktop;
};


export const statusOptions = [
  'Placed',
  'Accepted',
  'Preparing',
  'Ready',
  'Delivered',
  'Cancelled',
];

// Helper to get status colors and icons
export const getStatusInfo = (status: string) => {
  switch (status) {
    case 'Placed':
      return { text: 'Placed', color: 'text-orange-400', icon: Clock };
    case 'Accepted':
      return { text: 'Accepted', color: 'text-purple-400', icon: CheckCircle };
    case 'Preparing':
      return { text: 'Preparing', color: 'text-yellow-500', icon: ChefHat };
    case 'Ready':
      return { text: 'Ready', color: 'text-green-400', icon: Utensils };
    case 'Delivered':
      return { text: 'Delivered', color: 'text-green-500', icon: CheckCircle };
    case 'Completed':
      return { text: 'Completed', color: 'text-green-500', icon: CheckCircle };
    case 'Cancelled':
      return { text: 'Cancelled', color: 'text-red-500', icon: XCircle };
    default:
      return { text: 'Unknown', color: 'text-gray-400', icon: AlertCircle };
  }
};

// Logic for payment status display with updated icons
export const getPaymentStatusInfo = (isPaid: boolean) => {
  return isPaid
    ? {
        text: "Paid",
        color: "text-green-600 dark:text-green-300",
        icon: CheckCircle,
      }
    : {
        text: "Pending",
        color: "text-orange-600 dark:text-orange-300",
        icon: Clock,
      };
};